// Auth API surface for the admin app.
//
// M1 (motiflink §7): the admin has NO login form of its own — one unified
// sign-in lives in the consumer app, and the domain-wide cookie signs the
// user into every subdomain at once. This module only reads /auth/me,
// signs out, and knows where to bounce unauthenticated visitors.

import { api } from './api';

export interface MeUser {
  id: string;
  email: string;
  locale: 'ar' | 'en';
  emailVerifiedAt: string | null;
  displayName: string | null;
  // Effective roles from the JWT — the shell gates on staff roles.
  roles: string[];
}

const STAFF_ROLES = new Set(['super_admin', 'content_editor', 'support']);

export function isStaff(me: MeUser): boolean {
  return (me.roles ?? []).some((r) => STAFF_ROLES.has(r));
}

export async function getMe(signal?: AbortSignal): Promise<MeUser> {
  const { user } = await api<{ user: MeUser }>('/auth/me', { signal });
  return user;
}

export async function logout(): Promise<void> {
  await api('/auth/logout', { method: 'POST' });
}

/** The consumer app origin matching this admin deployment. */
export function appOrigin(): string {
  const host = window.location.hostname;
  if (host === 'admin.fajrarabi.com') return 'https://app.fajrarabi.com';
  if (host === 'staging-admin.fajrarabi.com') return 'https://staging-app.fajrarabi.com';
  return 'http://localhost:5175';
}

/** The consumer app's sign-in URL, carrying a redirect back to admin. */
export function appSignInUrl(redirectTo?: string): string {
  const target = redirectTo ?? window.location.href;
  return `${appOrigin()}/ar?redirect=${encodeURIComponent(target)}`;
}
